/**
 * Vello typography tokens (S055; IMPORT-0001 §3, DD-02).
 *
 * Families and type roles are verbatim from `tokens/typography.css`. Hanken Grotesk carries the UI; IBM
 * Plex Mono carries prices, plates, OTP/codes, and timers (tabular). The source loaded fonts through a
 * hardcoded Google Fonts `@import` (design-debt DD-02: offline/perf/residency risk); this package instead
 * describes the faces as a **self-hostable `FontFace` manifest** with no CDN URL, so the host app supplies
 * the woff2 files. `fontImportUrl` is retained only as documentation of the original source, never emitted.
 */
/** Font-family stacks (first family is the brand face; the rest are graceful fallbacks). */
export const FONT_FAMILIES = {
    sans: "'Hanken Grotesk', -apple-system, 'Helvetica Neue', Arial, sans-serif",
    mono: "'IBM Plex Mono', 'SF Mono', Menlo, monospace",
};
/**
 * The faces the design system needs, as a manifest to self-host (DD-02 resolved: no `@import`, no CDN).
 * The app registers these against bundled woff2 files; `@vello/tokens` never fetches a remote font.
 */
export const FONT_FACES = [
    { family: 'Hanken Grotesk', weights: [300, 400, 500, 600, 700, 800], display: 'swap' },
    { family: 'IBM Plex Mono', weights: [400, 500, 600, 700], display: 'swap' },
];
/** The original source's font URL, kept only for provenance. Emitting it is a DD-02 regression. */
export const LEGACY_FONT_IMPORT_URL = 'https://fonts.googleapis.com/css2?family=Hanken+Grotesk:wght@300;400;500;600;700;800&family=IBM+Plex+Mono:wght@400;500;600;700&display=swap';
/** The nine design-system roles (verbatim from `tokens/typography.css`) plus the web-console ramp (see {@link ConsoleTypeRoleName}). */
export const TYPE_ROLES = {
    hero: { sizePx: 56, leading: 1.04, weight: 800, trackingEm: -0.02 },
    display: { sizePx: 40, leading: 1.08, weight: 700, trackingEm: -0.015 },
    headline: { sizePx: 28, leading: 1.15, weight: 700, trackingEm: -0.01 },
    title: { sizePx: 20, leading: 1.25, weight: 600, trackingEm: -0.005 },
    'body-lg': { sizePx: 17, leading: 1.45, weight: 400 },
    body: { sizePx: 15, leading: 1.5, weight: 400 },
    label: { sizePx: 14, leading: 1.3, weight: 600 },
    caption: { sizePx: 13, leading: 1.4, weight: 400 },
    micro: { sizePx: 11, leading: 1.3, weight: 600, trackingEm: 0.08 },
    // -- The web-console ramp (A08). Verbatim sizes/weights from the design file; see ConsoleTypeRoleName. --
    'page-title': { sizePx: 24, leading: 1.15, weight: 700, trackingEm: -0.01 },
    'record-title': { sizePx: 22, leading: 1.2, weight: 700, trackingEm: -0.01 },
    'drawer-title': { sizePx: 19, leading: 1.2, weight: 700, trackingEm: -0.01 },
    'modal-title': { sizePx: 20, leading: 1.25, weight: 700, trackingEm: -0.01 },
    'modal-title-sm': { sizePx: 19, leading: 1.25, weight: 700, trackingEm: -0.01 },
    subtitle: { sizePx: 14, leading: 1.4, weight: 400 },
    'control-lg': { sizePx: 14.5, leading: 1.3, weight: 600 },
    control: { sizePx: 13.5, leading: 1.3, weight: 600 },
    'control-md': { sizePx: 13, leading: 1.3, weight: 600 },
    'control-sm': { sizePx: 12.5, leading: 1.3, weight: 600 },
    'btn-lg': { sizePx: 16, leading: 1.3, weight: 600 },
    pill: { sizePx: 12, leading: 1.2, weight: 600 },
    cell: { sizePx: 13.5, leading: 1.4, weight: 400 },
    foot: { sizePx: 12.5, leading: 1.4, weight: 500 },
    note: { sizePx: 11.5, leading: 1.45, weight: 400 },
    hint: { sizePx: 11.5, leading: 1.45, weight: 500 },
    badge: { sizePx: 10.5, leading: 1.4, weight: 600 },
    'empty-title': { sizePx: 14.5, leading: 1.3, weight: 600 },
    'palette-input': { sizePx: 16, leading: 1.3, weight: 400 },
    'kpi-label': { sizePx: 12, leading: 1.3, weight: 600 },
    'kpi-hint': { sizePx: 12, leading: 1.3, weight: 500 },
    'kpi-value': { sizePx: 20, leading: 1.1, weight: 700, mono: true },
    'kpi-tile-label': { sizePx: 11, leading: 1.3, weight: 600 },
    'kpi-tile-value': { sizePx: 16, leading: 1.1, weight: 700, mono: true },
    data: { sizePx: 13, leading: 1.4, weight: 500, mono: true },
    'col-head': { sizePx: 11, leading: 1.3, weight: 600, trackingEm: 0.08, mono: true },
    'mono-sm': { sizePx: 11, leading: 1.3, weight: 500, mono: true },
    eyebrow: { sizePx: 10.5, leading: 1.3, weight: 600, trackingEm: 0.1, mono: true },
};
/** The weight range the brand uses across roles (Hanken 300–800). A guard for `assertWeight`. */
export const WEIGHT_RANGE = { min: 300, max: 800 };
//# sourceMappingURL=typography.js.map